import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { writeFetch, getSession } from '../api';
import { getAgeBand } from '../utils/ageBands';

const GameContext = createContext();

const initialState = {
  user: null,
  activeKid: null,
  playerName: '',
  age: null,
  ageBand: null,
  score: 0,
  tokens: 100,
  level: 1,
  avatar: { character: 'lion', hat: null, accessory: null, background: 'jungle' },
  completedQuests: [],
  gameStats: { totalAnswers: 0, correctAnswers: 0 },
  isLoading: true,
};

function gameReducer(state, action) {
  switch (action.type) {
    case 'AUTH_SUCCESS':
      return { ...state, user: action.payload, isLoading: false };
    case 'KID_AUTH_SUCCESS': {
      const k = action.payload;
      const age = k.age || 7;
      return {
        ...state,
        activeKid: k,
        playerName: k.name,
        age,
        ageBand: getAgeBand(age),
        tokens: k.total_tokens || 100,
        completedQuests: typeof k.completed_quests === 'string' ? JSON.parse(k.completed_quests) : (k.completed_quests || []),
        gameStats: typeof k.game_stats === 'string' ? JSON.parse(k.game_stats) : (k.game_stats || initialState.gameStats),
        level: Math.floor((k.total_tokens || 100) / 200) + 1,
        avatar: { ...state.avatar, character: k.avatar_image || 'lion' },
        isLoading: false,
      };
    }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'ADD_SCORE': {
      const newScore = state.score + action.payload;
      const tokensEarned = Math.floor(action.payload / 2);
      return {
        ...state,
        score: newScore,
        tokens: state.tokens + tokensEarned,
        gameStats: {
          totalAnswers: state.gameStats.totalAnswers + 1,
          correctAnswers: state.gameStats.correctAnswers + 1,
        },
      };
    }
    case 'GAME_COMPLETED': {
      const qId = action.payload?.questId;
      const bonus = action.payload?.bonusTokens || 10;
      return {
        ...state,
        tokens: state.tokens + bonus,
        completedQuests: qId && !state.completedQuests.includes(qId) ? [...state.completedQuests, qId] : state.completedQuests,
      };
    }
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  useEffect(() => {
    async function init() {
      const session = getSession();
      if (!session) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      if (session.isKid) {
        dispatch({ type: 'KID_AUTH_SUCCESS', payload: session });
      } else {
        dispatch({ type: 'AUTH_SUCCESS', payload: session });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    }
    init();
  }, []);

  // Sync Kid state to DB
  useEffect(() => {
    async function sync() {
      if (!state.activeKid || state.isLoading) return;
      
      const payload = {
        total_tokens: state.tokens,
        completed_quests: JSON.stringify(state.completedQuests),
        game_stats: JSON.stringify(state.gameStats),
        avatar_image: state.avatar.character,
      };
      
      try {
        await writeFetch(`update/kids/${state.activeKid.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } catch (err) {
        console.error("Sync failed", err);
      }
    }
    
    if (state.activeKid) {
      const timer = setTimeout(sync, 2000);
      return () => clearTimeout(timer);
    }
  }, [state.tokens, state.completedQuests, state.gameStats, state.avatar]);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
}